import React, { useReducer, useEffect } from 'react';

import clsx from 'clsx';
import { concat } from 'lodash';
import PropTypes from 'prop-types';
import KeyStore from 'utils/KeyStore';
import update from 'immutability-helper';
import ModalUtils from 'utils/ModalUtils';
import Permission from 'utils/enum/Permissions';
import { ActivityType, EventStatus } from 'utils/enum/ActivitiesEnum';
import InfiniteScroll from 'components/widgets/InfiniteScroll';
import { ReactComponent as AddCircleOutlineIcon } from 'assets/addproduct.svg';
import EventActivityItem from 'components/modules/crm/activities/EventActivityItem';
import ActivitiesForm from 'components/modules/crm/activities/form/ActivitiesForm';
// Materia UI
import { makeStyles, IconButton, Tooltip } from '@material-ui/core';

// Apollo
import { useQuery, useSubscription } from '@apollo/client';
import SubscriptionActionType from 'utils/enum/SubscriptionActionType';
import ActivitiesQuery from 'services/graphQL/query/crm/ActivitiesQuery';
import ActivitySubscription from 'services/graphQL/subscription/crm/ActivitySubscription';
import { FetchPolicy } from 'utils/enum/Core';

const useStyles = makeStyles((theme) => ({
    body: {
        height: '100%',
        overflow: 'auto',
        overflowX: 'hidden',
    },
    header: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: theme.spacing(1),
    },
    opacity: {
        opacity: 0.5,
    },
    container: {
        height: '100%',
        padding: theme.spacing(2),
    },
}));

const ACTION_TYPE = {
    SET_OFFSET: 'setOffset',
    ADD_RECORD: 'addRecord',
    SET_RECORDS: 'setRecords',
    TOGGLE_MODAL: 'toggleModal',
    UPDATE_RECORD: 'updateRecord',
    DELETE_RECORD: 'deleteRecord',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.TOGGLE_MODAL:
        return update(state, {
            open: { $set: action.open },
            isEditing: { $set: action.isEditing },
            activityId: { $set: action.activityId },
        });
    case ACTION_TYPE.SET_RECORDS:
        const records = concat(state.records, action.payload?.data);
        return update(state, {
            records: { $set: records },
            totalCount: { $set: action.payload?.totalCount },
        });
    case ACTION_TYPE.DELETE_RECORD:
        const activityIndex = state.records.findIndex((item) => item.activityId === action.payload?.data?.activityId);

        if (activityIndex >= 0) {
            return update(state, {
                records: { $splice: [[activityIndex, 1]] },
                totalCount: { $set: state.totalCount - 1 },
            });
        }

        return state;
    case ACTION_TYPE.UPDATE_RECORD:
        const index = state.records.findIndex((item) => item.activityId === action?.payload?.data?.activityId);
        if (index >= 0) {
            return update(state, {
                records: { [index]: { $set: action?.payload?.data || {} } },
            });
        }
        return state;
    case ACTION_TYPE.ADD_RECORD:
        return update(state, {
            records: { $unshift: [action?.payload?.data] },
            totalCount: { $set: state.totalCount + 1 },
        });
    case ACTION_TYPE.SET_OFFSET:
        return update(state, {
            offset: { $set: action.value },
        });
    default:
        return state;
    }
};

const EventActivity = ({ crmId, lotId }) => {
    const classes = useStyles();
    const keyStore = new KeyStore();
    const [state, dispatch] = useReducer(reducer, {
        open: false,
        records: [],
        totalCount: 0,
        offset: 0,
    });
    const {
        data, error, loading,
    } = useQuery(ActivitiesQuery.GET_EVENT_ACTIVITIES_BY_CRMID,
        {
            variables: { crmId, paging: { start: state.offset, limit: 25 }, active: true },
            fetchPolicy: FetchPolicy.NETWORK_ONLY,
        });

    const { data: subscriptionData } = useSubscription(ActivitySubscription.UPDATED_ACTIVITY_EVENT, {
        variables: {
            crmId,
        },
        shouldResubscribe: true,
    });
    const CRM_LEAD_ACTIVITY_WRITE = keyStore.hasPermission(Permission.CRM_LEAD_ACTIVITY_WRITE);

    useEffect(() => {
        if (!loading) {
            dispatch({
                type: ACTION_TYPE.SET_RECORDS,
                payload: data?.getEventActivitiesByCRMId,
            });
        }
    }, [data, loading]);

    useEffect(() => {
        if (subscriptionData) {
            if (subscriptionData.updatedActivityEvent.type === SubscriptionActionType.ADDED) {
                dispatch({
                    type: ACTION_TYPE.ADD_RECORD,
                    payload: subscriptionData.updatedActivityEvent,
                });
            }

            if (subscriptionData.updatedActivityEvent.type === SubscriptionActionType.UPDATED) {
                dispatch({
                    type: ACTION_TYPE.UPDATE_RECORD,
                    payload: subscriptionData.updatedActivityEvent,
                });
            }

            if (subscriptionData.updatedActivityEvent.type === SubscriptionActionType.DELETED) {
                dispatch({
                    type: ACTION_TYPE.DELETE_RECORD,
                    payload: subscriptionData.updatedActivityEvent,
                });
            }
        }
    }, [subscriptionData]);

    const onClose = () => {
        dispatch({
            type: ACTION_TYPE.TOGGLE_MODAL,
            open: false,
            isEditing: false,
            activityId: null,
        });
    };

    const onOpen = (record, isEditing = false) => {
        dispatch({
            type: ACTION_TYPE.TOGGLE_MODAL,
            open: true,
            isEditing,
            activityId: record.activityId,
        });
    };

    const loadMore = () => {
        const currentOffset = state.records.length;
        dispatch({ type: ACTION_TYPE.SET_OFFSET, value: currentOffset });
    };

    if (error) {
        ModalUtils.errorMessage(error.graphQLErrors);
        return null;
    }

    const isAddDisabled = (state.records ?? []).some((record) => [EventStatus.SCHEDULED, EventStatus.CONFIRMED].includes(record.event.status));
    return (
        <div className={clsx('d-flex-column', classes.container)}>
            {CRM_LEAD_ACTIVITY_WRITE && (
                <div className={clsx(classes.header, isAddDisabled ? classes.opacity : '')}>
                    <Tooltip placement="top" title={isAddDisabled ? 'There is already a Scheduled or Confirmed appointment' : ''}>
                        <span>
                            <IconButton
                                disabled={isAddDisabled}
                                size="small"
                                aria-label="Add"
                                onClick={onOpen}
                            >
                                <AddCircleOutlineIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
            )}
            <InfiniteScroll
                lengthRecord={state?.records.length}
                totalRecord={state?.totalCount}
                loadMore={loadMore}
                load={loading}
                loadAtScrollPercent={80}
            >
                {
                    state.records.map((item, index) => (
                        <EventActivityItem
                            record={item}
                            key={index}
                            onOpen={onOpen}
                        />
                    ))
                }
            </InfiniteScroll>
            {state.open && (
                <ActivitiesForm
                    crmId={crmId}
                    lotId={lotId}
                    open={state.open}
                    onClose={onClose}
                    isEditing={state.isEditing}
                    activityId={state.activityId}
                    type={ActivityType.APPOINTMENT}
                />
            )}
        </div>
    );
};

EventActivity.propTypes = {
    crmId: PropTypes.string.isRequired,
    lotId: PropTypes.number,
};

EventActivity.defaultProps = {
    lotId: null,
};

export default EventActivity;
